import {
  Badge,
  Box,
  Button,
  chakra,
  Divider,
  Flex,
  HStack,
  Image,
  keyframes,
  Link,
  ListItem,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tag,
  TagLabel,
  Text,
  UnorderedList,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import Header from '../../../../../components/Header/Header';
import CardHeaderMobile from '../../../../../components/Mobile/header';
import { IDetailSelectiveProcess } from '../../../../../services/hooks/SelectiveProcess/Types';
import { Tabs } from '@chakra-ui/react';
import pcdLogo from '../../../../../assets/pcd.svg';
import { TbFileDescription } from 'react-icons/tb';
import { RiMoneyDollarCircleLine } from 'react-icons/ri';
import { FiCalendar } from 'react-icons/fi';
import { FaUserGroup } from 'react-icons/fa6';
import { badgeColor } from '../../../../../utils/badgeFunction';
import { IoDocumentAttachOutline } from 'react-icons/io5';
import { CgFeed } from 'react-icons/cg';
import { BiCommentDetail, BiLink } from 'react-icons/bi';
import LabelWithValueMobilePage, { LabelWithValueBreakArrow } from '../../../../../components/Mobile/labelWithValue';
import NavigationButtonSPDetailMobilePage from './navigateButtons';
import AlertMobile from '../../../../../components/Mobile/alert';
import CardVacancyMobilePage from './cardVacancy';
import { createPortal } from 'react-dom';
import { MdKeyboardDoubleArrowUp } from 'react-icons/md';
import { useContext, useRef, useState } from 'react';
import { ContextAuth } from '../../../../../contexts/Authentication';

const floatIn = keyframes`
      from { opacity: 0; transform: translateY(18px) scale(0.995); }  
  `;

const AnimatedBox = chakra(Box, {
  baseStyle: {
    animation: `${floatIn} 420ms cubic-bezier(.2,.9,.3,1) both`,
  },
});

interface IOwnProps {
  isLoading: boolean;
  data?: IDetailSelectiveProcess;
  onSubmitExemption(data?: IDetailSelectiveProcess): void;
  onSubmitImpugnation(data?: IDetailSelectiveProcess): void;
  onSubmitRegistration(data?: IDetailSelectiveProcess): void;
}

const SelectiveProcessDetailMobilePage: React.FC<IOwnProps> = ({
  isLoading,
  data,
  onSubmitExemption,
  onSubmitImpugnation,
  onSubmitRegistration,
}) => {
  const bgGradient = useColorModeValue('linear(to-br, gray.50, white)', 'linear(to-br, gray.900, gray.800)');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const [buttonToTop, setButtonToTop] = useState<boolean>(false);
  const refDiv = useRef<HTMLDivElement>(null);
  const { isAuth, candidate } = useContext(ContextAuth);

  const canShowButton = () => {
    if (!isAuth) return true;

    if (candidate && !data?.allows_foreigners && candidate?.is_foreign) return false;

    return true;
  };

  return (
    <>
      <Stack h="100%" top={0} w="100%" bg="white" gap={0}>
        <Header />

        <VStack
          mt={0}
          flex={1}
          py={2}
          overflow="auto"
          onScroll={(value) => setButtonToTop(value.currentTarget.scrollTop > 500 ? true : false)}
          ref={refDiv}
          w="100%"
        >
          {(isLoading && (
            <VStack flex={1} w="100%" justify="center">
              <Spinner emptyColor="gray.100" color="blue.500" size="xl" />
              <Text fontSize="sm">Buscando dados do processo seletivo...</Text>
            </VStack>
          )) || (
            <AnimatedBox
              bgGradient={bgGradient}
              borderRadius="lg"
              boxShadow="lg"
              mx={3}
              p={{ base: 5, md: 10 }}
              borderWidth="1px"
              borderColor={borderColor}
              bg="white"
              flex={1}
              display="flex"
              flexDir="column"
            >
              <VStack boxSizing="border-box" maxW="lg" spacing={4} align="stretch" flex={1}>
                <CardHeaderMobile
                  title="Processo seletivo"
                  subTitle={`Aqui você encontra todas as informações do processo seletivo: prazos de inscrição e isenção, acesso ao edital, publicações oficiais e a listagem completa das ${data?.is_cultural_house ? 'turmas' : 'vagas'} disponíveis.`}
                  badgeText="Detalhes"
                  badgeIcon={<BiCommentDetail size={14} />}
                  px={3}
                  gap={1}
                  colorScheme="green"
                />
                <Tag size="sm" w="fit-content" variant="outline" colorScheme="purple">
                  <TagLabel>{data?.selective_process_type}</TagLabel>
                </Tag>
                <Text
                  bgColor="primaryColor"
                  color="white"
                  boxSizing="border-box"
                  py={1}
                  px={5}
                  fontWeight="bold"
                  fontSize="small"
                  textTransform="uppercase"
                >
                  {data?.name}
                </Text>
                {data && data.alert_text && (
                  <AlertMobile title="Aviso" mx={0}>
                    {data.alert_text}
                  </AlertMobile>
                )}
                <VStack spacing={3} align="stretch" flex={1}>
                  <Flex gap={1} mb={2} flexWrap="wrap">
                    {data?.general_info.status.map((situation) => {
                      const colorBadge = badgeColor(situation.name);

                      return (
                        <Badge
                          key={`${situation.name}-${situation.id}`}
                          bgColor={colorBadge?.bgColor}
                          color={colorBadge?.color}
                          px={2}
                          py={1}
                          borderRadius="md"
                          display="flex"
                          alignItems="center"
                          w="fit-content"
                          gap={2}
                        >
                          <Text fontSize="0.63rem" as="span">
                            {situation.name}
                          </Text>
                        </Badge>
                      );
                    })}
                  </Flex>
                  <Stack>
                    <HStack>
                      <Box w="17px">
                        <TbFileDescription size={19} />
                      </Box>
                      <LabelWithValueMobilePage label="Edital" value={data?.general_info.notice_number} />
                    </HStack>
                    {data?.exemption_period && (
                      <LabelWithValueBreakArrow
                        icon={<RiMoneyDollarCircleLine size={18} />}
                        label="Período de isenções"
                        value={data.exemption_period}
                      />
                    )}
                    {data?.registration_period && (
                      <LabelWithValueBreakArrow
                        icon={<FiCalendar size={17} />}
                        label=" Período de inscrições"
                        value={data.registration_period}
                      />
                    )}
                    {(data?.general_info.has_black_or_brown || data?.general_info.has_pcd) && (
                      <HStack align="flex-start" flexDir="column" gap={3}>
                        {data.general_info.has_pcd && (
                          <LabelWithValueMobilePage
                            label="Pessoas com deficiência (PCDs)"
                            icon={<Image src={pcdLogo} w="18px" h="18px" />}
                            hiddenColon
                          />
                        )}

                        {data.general_info.has_black_or_brown && (
                          <LabelWithValueMobilePage
                            label="Cotas raciais"
                            icon={<FaUserGroup size={18} />}
                            hiddenColon
                          />
                        )}
                      </HStack>
                    )}
                  </Stack>
                  {(data?.general_info.open_exemption ||
                    data?.general_info.open_impugnation ||
                    data?.general_info.open_registration) && (
                    <Stack mt={2}>
                      {data?.general_info.open_impugnation && (
                        <>
                          {canShowButton() && (
                            <NavigationButtonSPDetailMobilePage
                              colorSchema={{
                                main: '#2d66bb',
                                secondaryInitial: '#1b3e73',
                                secondaryFinal: '#4d88d9',
                              }}
                              onClick={() => onSubmitImpugnation(data)}
                            >
                              Impugnação contra o edital
                            </NavigationButtonSPDetailMobilePage>
                          )}
                        </>
                      )}
                      {data?.general_info.open_exemption && (
                        <>
                          {canShowButton() && (
                            <NavigationButtonSPDetailMobilePage
                              colorSchema={{
                                main: '#9e1a1a',
                                secondaryInitial: '#671111',
                                secondaryFinal: '#d13434',
                              }}
                              onClick={() => onSubmitExemption(data)}
                            >
                              Solicitação de isenção
                            </NavigationButtonSPDetailMobilePage>
                          )}
                        </>
                      )}
                      {data?.general_info.open_registration && (
                        <>
                          {canShowButton() && (
                            <NavigationButtonSPDetailMobilePage
                              colorSchema={{
                                main: '#008c21',
                                secondaryInitial: '#006617',
                                secondaryFinal: '#33a94b',
                              }}
                              onClick={() => onSubmitRegistration(data)}
                            >
                              Solicitação de inscrição
                            </NavigationButtonSPDetailMobilePage>
                          )}
                        </>
                      )}
                    </Stack>
                  )}

                  {((data?.publications?.notices && data?.publications.notices.length > 0) ||
                    (data?.publications?.publications && data.publications.publications.length > 0)) && (
                    <>
                      <Divider mt={3} mb={0} boxSizing="border-box" borderColor="gray.300" />
                      <Tabs>
                        <TabList>
                          {data?.publications?.notices && data?.publications.notices.length > 0 && (
                            <Tab fontSize="sm" gap={1}>
                              <IoDocumentAttachOutline size={15} />
                              Editais e aditivos
                            </Tab>
                          )}
                          {data?.publications?.publications && data.publications.publications.length > 0 && (
                            <Tab fontSize="sm" gap={1}>
                              <CgFeed size={16} />
                              Publicações
                            </Tab>
                          )}
                        </TabList>
                        <TabPanels>
                          {data?.publications?.notices && data.publications.notices.length > 0 && (
                            <TabPanel px={0} pb={0}>
                              <Stack>
                                {data?.publications.notices && (
                                  <UnorderedList p={0} ml={0}>
                                    {data?.publications.notices?.map((attach, index) => (
                                      <ListItem listStyleType="''" key={index} p={0} m={0}>
                                        <Flex
                                          color="colorLink"
                                          gap={2}
                                          align="center"
                                          padding="5px 13px"
                                          borderRadius="sm"
                                          {...(index % 2 === 0 && { bgColor: '#f3f3f3' })}
                                        >
                                          <Box>
                                            <BiLink />
                                          </Box>
                                          <Box>
                                            <Link target="_blank" href={attach.url || attach.file}>
                                              <Stack gap={0}>
                                                <Text fontSize="0.8rem">{attach.subtitle}</Text>
                                                <Text fontSize="0.6rem" mt={0} color="black" as="span">
                                                  ({attach.created_at || ''})
                                                </Text>
                                              </Stack>
                                            </Link>
                                            {attach?.details && (
                                              <Text color={'black'} fontSize="0.6rem">
                                                {attach.details}
                                              </Text>
                                            )}
                                          </Box>
                                        </Flex>
                                      </ListItem>
                                    ))}
                                  </UnorderedList>
                                )}
                              </Stack>
                            </TabPanel>
                          )}
                          {data?.publications?.publications && data.publications.publications.length > 0 && (
                            <TabPanel px={0} pb={0}>
                              <Stack>
                                {data?.publications.publications && (
                                  <UnorderedList p={0} ml={0}>
                                    {data?.publications.publications?.map((attach, index) => (
                                      <ListItem listStyleType="''" key={index} p={0} m={0}>
                                        <Flex
                                          color="colorLink"
                                          gap={2}
                                          align="center"
                                          padding="5px 13px"
                                          borderRadius="sm"
                                          {...(index % 2 === 0 && { bgColor: 'gray.100' })}
                                        >
                                          <Box>
                                            <BiLink />
                                          </Box>
                                          <Box>
                                            <Link target="_blank" href={attach.url || attach.file}>
                                              <Stack gap={0}>
                                                <Text fontSize="0.8rem">{attach.subtitle}</Text>
                                                <Text fontSize="0.6rem" mt={0} color="black" as="span">
                                                  ({attach.created_at || ''})
                                                </Text>
                                              </Stack>
                                            </Link>
                                            {attach?.details && (
                                              <Text color={'black'} fontSize="0.6rem">
                                                {attach.details}
                                              </Text>
                                            )}
                                          </Box>
                                        </Flex>
                                      </ListItem>
                                    ))}
                                  </UnorderedList>
                                )}
                              </Stack>
                            </TabPanel>
                          )}
                        </TabPanels>
                      </Tabs>
                    </>
                  )}
                  <Divider mt={2} mb={4} boxSizing="border-box" borderColor="gray.300" />
                  <Text
                    bgColor="primaryColor"
                    color="white"
                    boxSizing="border-box"
                    py={1}
                    px={5}
                    fontWeight="bold"
                    textAlign="center"
                    fontSize="sm"
                    textTransform="uppercase"
                  >
                    {data?.is_cultural_house ? 'TURMAS' : 'VAGAS'}
                  </Text>
                  {data?.vacancy_observations && (
                    <AlertMobile title="Aviso" mx={0}>
                      {data?.vacancy_observations}
                    </AlertMobile>
                  )}
                  {data?.vacancies.map((vacancy) => <CardVacancyMobilePage key={vacancy.id} item={vacancy} />)}
                </VStack>
              </VStack>
            </AnimatedBox>
          )}
        </VStack>
      </Stack>
      {buttonToTop &&
        createPortal(
          <Button
            borderRadius="full"
            w="50px"
            h="50px"
            position="absolute"
            bg="secondaryColor"
            bottom={import.meta.env.DEV ? '120px' : 3}
            right={3}
            onClick={() => refDiv.current?.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <Box>
              <MdKeyboardDoubleArrowUp size={26} color="white" />
            </Box>
          </Button>,
          document.body,
        )}
    </>
  );
};

export default SelectiveProcessDetailMobilePage;
